import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { LeakSiteDossierRow } from './LeakSiteDossierRow'
import type { DossierClaim } from './leak-site-utils'

/**
 * Renders the dossier feed and its loading / error / empty states. Skeletons
 * are shaped like dossier rows; the empty state explains how to widen the
 * window or clear filters.
 */

export interface LeakSiteDossierListProps {
  rows: DossierClaim[]
  expandedId: string | null
  onToggle: (id: string) => void
  isLoading: boolean
  isError: boolean
  hasActiveFilters: boolean
  onResetFilters: () => void
}

export function LeakSiteDossierList({
  rows,
  expandedId,
  onToggle,
  isLoading,
  isError,
  hasActiveFilters,
  onResetFilters,
}: LeakSiteDossierListProps) {
  if (isLoading) {
    return (
      <div className="space-y-2.5" data-testid="leak-sites-feed-loading">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-28 w-full rounded-lg" />
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <div
        role="alert"
        className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm"
        data-testid="leak-sites-feed-error"
      >
        <p className="font-medium text-foreground">Couldn’t load leak-site claims.</p>
        <p className="mt-1 text-muted-foreground">
          The claims feed failed to load. Adjust the time window or filters and
          try again.
        </p>
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <div
        className="rounded-lg border border-dashed border-border p-10 text-center"
        data-testid="leak-sites-feed-empty"
      >
        <p className="text-sm font-medium text-foreground">
          No claims match these filters
        </p>
        <p className="mx-auto mt-1.5 max-w-sm text-xs leading-relaxed text-muted-foreground">
          Try widening the time window, removing a sector or region filter, or
          clearing the search and quick chips.
        </p>
        {hasActiveFilters ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={onResetFilters}
          >
            Reset filters
          </Button>
        ) : null}
      </div>
    )
  }

  return (
    <div className="space-y-2.5" data-testid="leak-sites-feed">
      {rows.map((claim) => (
        <LeakSiteDossierRow
          key={claim.id}
          claim={claim}
          expanded={expandedId === claim.id}
          onToggle={() => onToggle(claim.id)}
        />
      ))}
    </div>
  )
}
